import React, { useMemo } from 'react';
import type { GridColDef } from '@mui/x-data-grid';
import { SurveyType } from '../../../../../shared/constants';
import {
  SURVEY_SCALE_BY_TYPE,
  surveyRowId,
} from '../../../../../shared/surveys';
import { parseDateToLocal } from '../../../../../shared/utils';
import CustomerCareDTable from '../../../../Shared/CustomerCareDTable';
import SurveyResponseChip from '../OverviewTab/SurveyResponseChip';
import type { CustomerSurveyResponse } from '../../../../../store/customers/customerTypes';
import { surveyRowLabel } from './surveyInsights';

interface SurveyScoresTableProps {
  rows: readonly CustomerSurveyResponse[];
  surveyType: SurveyType;
  pageSize?: number;
}

// Reads the API's can_reach_out flag for the table cell. Only an explicit
// "yes" is shown; false and null both render empty, because the flag is not
// collected on every answer and an empty cell must not read as "No".
export const reachOutLabel = (
  canReachOut: boolean | null | undefined,
): string => (canReachOut === true ? 'Yes' : '');

// The responses for one survey type in the selected window, newest first.
// Same table and chip the Overview CSAT / Wellness panels use, plus the raw
// score column. The CSAT table also shows whether the patient agreed to be
// reached out to about that answer, so care staff know who wants a follow-up.
const SurveyScoresTable: React.FC<SurveyScoresTableProps> = ({
  rows,
  surveyType,
  pageSize = 10,
}) => {
  const { max } = SURVEY_SCALE_BY_TYPE[surveyType];
  const showReachOut = surveyType === SurveyType.HubOverallExperience;

  // Stable column definitions so the data grid does not reset column state
  // on every render.
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'response',
        headerName: 'Survey response',
        flex: 2,
        renderCell: (params: any) => (
          <SurveyResponseChip
            response={surveyRowLabel(params.row, surveyType)}
            isCurrent
          />
        ),
      },
      {
        field: 'score',
        headerName: 'Score',
        flex: 1,
        renderCell: (params: any) => (
          <span>
            {params.row.score == null ? 'N/A' : `${params.row.score} / ${max}`}
          </span>
        ),
      },
      {
        field: 'received_at',
        headerName: 'Response time',
        flex: 2,
        renderCell: (params: any) => (
          <span>
            {params.row.received_at
              ? parseDateToLocal(
                  params.row.received_at,
                  'yyyy-MM-dd hh:mm:ss a',
                )
              : 'N/A'}
          </span>
        ),
      },
      ...(showReachOut
        ? [
            {
              field: 'can_reach_out',
              headerName: 'Can reach out',
              flex: 1,
              renderCell: (params: any) => (
                <span>{reachOutLabel(params.row.can_reach_out)}</span>
              ),
            } as GridColDef,
          ]
        : []),
    ],
    [surveyType, max, showReachOut],
  );

  const tableRows = rows.map((row) => ({ ...row, id: surveyRowId(row) }));

  return (
    <CustomerCareDTable
      rows={tableRows}
      columns={columns}
      pageSize={pageSize}
      defaultSortField="received_at"
      defaultSortDirection="desc"
      hideViewAllButton
    />
  );
};

export default SurveyScoresTable;
